import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { capture } from "../../../analytics/posthog";
import { useScrollLock, useIsMobile } from "../../../hooks/useScrollLock";
import { useSignalValue } from "../../../utils/signal";
import {
  bankrollSignal,
  getBankRollData,
} from "../positive-ev/components/bankroll/bankroll-form";
import {
  combinedParlayDecimalOdds,
  combinedParlayEvPct,
  combinedParlayTrueProb,
} from "../parlay-builder/parlay-engine";
import {
  ParlayLeg,
  SHARE_PATH,
  SharedParlayData,
  decimalToAmerican,
  encodeParlayForShare,
} from "../parlay-builder/parlay-share-utils";
import {
  generateParlayShareUrl,
  webShareOrCopy,
} from "../../../services/share-service";
import BookLogo from "../parlay-builder/book-logo";
import { trackBet } from "../my-bets/myBetsApi";
import { noteTrackedParlay } from "../my-bets/tracked-bets-store";
import { promptBoardToast } from "../common/board-toast";
import { useSwipeDismissSheet } from "../common/useSwipeDismissSheet";
import {
  activeSlipBookSignal,
  clearBookSlip,
  normBook,
  parlaySlipOpenSignal,
  removeSlipLeg,
  replaceBookSlip,
  requoteLeg,
  saveParlaySnapshot,
  slipLegKey,
  useSlipLegs,
} from "./parlay-slip-store";


/** Stake share when Kelly finds no edge at market prices. */
const UNIT_SHARE = 0.01;
/**
 * App-root host for the on-the-fly EV parlay slips. A parlay lives at
 * one book, so there is one slip PER BOOK — the dock shows the active
 * book's slip with tabs to switch when several books hold legs, and a
 * price-comparison section that re-quotes the whole slip at any other
 * book quoting all its legs (fair odds are book-independent, so only
 * odds/EV move). Collapsed it's a floating pill; expanded it's a
 * bottom-right card (desktop) or bottom sheet (mobile) that does all
 * the math: combined odds, hit chance, parlay EV%, and a Kelly-sized
 * stake from the user's saved bankroll. "Track parlay" files the slip
 * in My Bets as one manually-graded bet.
 */

const fmtAmerican = (n: number): string =>
  Number.isFinite(n) ? (n > 0 ? `+${Math.round(n)}` : `${Math.round(n)}`) : "—";

const fmtUsd = (n: number): string =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const truncate = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max - 1)}…`;

/** Earliest leg start, so the tracked parlay locks edits when games begin. */
function earliestStart(legs: ParlayLeg[]): string | null {
  let best: string | null = null;
  let bestMs = Infinity;
  for (const leg of legs) {
    const ms = Date.parse(leg.eventDate);
    if (Number.isFinite(ms) && ms < bestMs) {
      bestMs = ms;
      best = leg.eventDate;
    }
  }
  return best;
}

interface BookSlip {
  key: string;
  label: string;
  legs: ParlayLeg[];
}

const ParlaySlipDock: React.FC = () => {
  const navigate = useNavigate();
  const legs = useSlipLegs();
  const open = useSignalValue(parlaySlipOpenSignal);
  const activeBookRaw = useSignalValue(activeSlipBookSignal);
  const isMobile = useIsMobile(767);
  const bankrollValue = useSignalValue(bankrollSignal);

  const [stake, setStake] = useState("");
  const [stakeTouched, setStakeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareBusy, setShareBusy] = useState<"link" | "feed" | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Share URL per leg-set — one POST even if both share buttons are
   *  used (creates count against a daily limit). */
  const shareUrlRef = useRef<{ key: string; url: string } | null>(null);

  // Body scroll only needs locking when the sheet overlays the page.
  useScrollLock(open && isMobile && legs.length > 0);

  // Swipe-down-to-dismiss on the mobile sheet, same gesture as the
  // sharp detail and other bottom sheets (grip/header drags; the legs
  // list joins in when scrolled to the top).
  const swipe = useSwipeDismissSheet(open && isMobile && legs.length > 0, () => {
    parlaySlipOpenSignal.value = false;
  });

  useEffect(() => {
    void getBankRollData();
  }, []);

  // One slip per book, in first-leg-added order.
  const books = useMemo<BookSlip[]>(() => {
    const map = new Map<string, BookSlip>();
    for (const leg of legs) {
      const key = normBook(leg.bookie);
      const slip = map.get(key);
      if (slip) slip.legs.push(leg);
      else map.set(key, { key, label: leg.bookie, legs: [leg] });
    }
    return Array.from(map.values());
  }, [legs]);

  const active =
    (activeBookRaw && books.find((b) => b.key === normBook(activeBookRaw))) ||
    books[0] ||
    null;
  const slipLegs = useMemo(() => active?.legs ?? [], [active]);

  const math = useMemo(() => {
    const decimal = combinedParlayDecimalOdds(slipLegs);
    const trueProb = combinedParlayTrueProb(slipLegs);
    const evPct = combinedParlayEvPct(slipLegs);
    const american = slipLegs.length ? decimalToAmerican(decimal) : 0;

    const bankroll = bankrollValue?.bankroll && bankrollValue.bankroll > 0
      ? bankrollValue.bankroll
      : 2000;
    const kellyMult = bankrollValue?.kelly_multiple && bankrollValue.kelly_multiple > 0
      ? bankrollValue.kelly_multiple
      : 0.25;
    // Same Kelly the parlay builder's results panel applies.
    const b = decimal - 1;
    const fullKelly = b > 0 ? Math.max(0, Math.min((b * trueProb - (1 - trueProb)) / b, 1)) : 0;
    const kellyStake = bankroll * Math.min(fullKelly * kellyMult, 1);
    // No edge at market prices (BPI and other model picks): Kelly says $0, so
    // prefill one unit, 1% of bankroll, rather than leave the stake blank.
    const unit = slipLegs.length > 0 && fullKelly <= 0;
    const recommendedStake = unit ? bankroll * UNIT_SHARE : kellyStake;

    const events = slipLegs.map((l) => l.event);
    const hasSameEventLegs = new Set(events).size < events.length;
    return { decimal, trueProb, evPct, american, bankroll, kellyMult, recommendedStake, unit, hasSameEventLegs };
  }, [slipLegs, bankrollValue]);

  /** The same slip re-quoted at every other book quoting ALL its legs,
   *  best parlay EV first — "what would this pay elsewhere". */
  const requotes = useMemo(() => {
    if (slipLegs.length === 0 || !active) return [];
    const out: Array<{ key: string; label: string; american: number; evPct: number; legs: ParlayLeg[] }> = [];
    const candidates = new Map<string, string>();
    for (const alt of slipLegs[0].altBooks ?? []) {
      candidates.set(normBook(alt.bookie), alt.bookie);
    }
    candidates.delete(active.key);
    for (const [key, label] of candidates) {
      const requoted: ParlayLeg[] = [];
      for (const leg of slipLegs) {
        const r = requoteLeg(leg, key);
        if (!r) break;
        requoted.push(r);
      }
      if (requoted.length !== slipLegs.length) continue;
      out.push({
        key,
        label,
        legs: requoted,
        american: decimalToAmerican(combinedParlayDecimalOdds(requoted)),
        evPct: combinedParlayEvPct(requoted),
      });
    }
    return out.sort((a, b) => b.evPct - a.evPct).slice(0, 4);
  }, [slipLegs, active]);

  // Prefill/refresh the stake with the Kelly rec until the user types.
  useEffect(() => {
    if (stakeTouched) return;
    const rec = Math.round(math.recommendedStake);
    setStake(rec >= 1 ? String(rec) : "");
  }, [math.recommendedStake, stakeTouched]);

  // Each book's slip sizes independently — a fresh slip, a fresh stake.
  useEffect(() => {
    setStakeTouched(false);
    setError(null);
  }, [active?.key]);

  useEffect(() => {
    if (legs.length === 0) {
      setStakeTouched(false);
      setError(null);
      // Next slip starts collapsed, not wherever the last one ended.
      parlaySlipOpenSignal.value = false;
    }
  }, [legs.length]);

  if (legs.length === 0 || !active) return null;

  /** How much EV the best OTHER complete-quote book adds over the
   *  active slip — the "something obviously better exists" signal. */
  const bestUpgradePct = requotes.length > 0 ? requotes[0].evPct - math.evPct : 0;

  const stakeNumber = Number(stake);
  const stakeValid = Number.isFinite(stakeNumber) && stakeNumber > 0;
  const toWin = stakeValid ? stakeNumber * (math.decimal - 1) : 0;


  const setOpen = (next: boolean) => {
    parlaySlipOpenSignal.value = next;
    if (next) capture("parlay_slip_opened", { legs: slipLegs.length, books: books.length });
  };

  const switchBook = (requote: { label: string; legs: ParlayLeg[] }) => {
    if (!replaceBookSlip(active.label, requote.legs)) {
      setError(`${requote.label} slip is full.`);
      return;
    }
    capture("parlay_slip_requoted", { to: requote.label, legs: requote.legs.length });
  };

  const trackParlay = async () => {
    if (!localStorage.getItem("access_token")) {
      navigate("/login");
      return;
    }
    if (!stakeValid) {
      setError("Enter a stake.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const sports = Array.from(new Set(slipLegs.map((l) => l.sport).filter(Boolean)));
      const result = await trackBet({
        odd_id: null,
        league: sports.length === 1 ? sports[0] : "PARLAY",
        market_key: null,
        market: `Parlay (${slipLegs.length} legs)`,
        selection: null,
        bet_type: null,
        selection_name: truncate(slipLegs.map((l) => l.pick).join(" + "), 240),
        bookmaker_key: active.label,
        odds_american: Math.round(math.american),
        stake_amount: stakeNumber,
        event: truncate(Array.from(new Set(slipLegs.map((l) => l.event))).join(" · "), 240),
        event_start_time: earliestStart(slipLegs),
        ev_percent: math.evPct,
        fair_odds_american: Math.round(decimalToAmerican(1 / Math.max(math.trueProb, 1e-9))),
        is_live: false,
        source: "parlay-slip",
        // The backend stores these and expands them into per-leg
        // fingerprint rows, so each leg's board row shows BET.
        // The backend freezes each leg's full identity from these, so
        // the parlay can still grade after the odds feed rotates the
        // legs out (it purges a game's rows 4h after it starts).
        legs: slipLegs.map((l) => ({
          odd_id: l.oddId ?? null,
          market_key: l.marketKey ?? null,
          selection_name: l.pick,
          odds_american: Number.isFinite(l.odds) && l.odds !== 0 ? Math.round(l.odds) : null,
          league: l.sport || null,
          event: l.event || null,
          event_start_time: l.eventDate || null,
          market: l.market || null,
        })),
      });
      capture("parlay_slip_tracked", {
        legs: slipLegs.length,
        ev_pct: math.evPct,
        odds_american: Math.round(math.american),
        book: active.label,
      });
      // The rich legs (odds/fair/altBooks) only exist client-side, so
      // snapshot them per bet id to power My Bets' "Copy to slip".
      if (result.created && result.bet?.id != null) {
        saveParlaySnapshot(result.bet.id, slipLegs);
      }
      // Flip every leg's board row to BET right away — the server echoes
      // the same keys from legs_json on the next fingerprint hydration.
      noteTrackedParlay(
        slipLegs.map((l) => ({ oddId: l.oddId, selectionName: l.pick }))
      );
      // Tracked = done: clear this book's slip so the next parlay starts
      // on a fresh ticket (other books' slips are untouched).
      clearBookSlip(active.label);
      promptBoardToast({
        message: result.created
          ? "Parlay tracked. Grade it in My Bets after the games."
          : "Already in your tracker.",
        actionLabel: "My Bets →",
        actionTo: "/my-bets",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Create (or reuse) the share link for the active slip — the same
   * /parlay-builder/share/:id pages the builder generates, so the OG
   * preview card and share landing page come for free.
   */
  const ensureShareUrl = async (): Promise<string | null> => {
    if (!localStorage.getItem("access_token")) {
      navigate("/login");
      return null;
    }
    const key = slipLegs.map(slipLegKey).join("|");
    if (shareUrlRef.current?.key === key) return shareUrlRef.current.url;

    const payload: SharedParlayData = {
      s: "mixed",
      b: active.key,
      n: active.label,
      l: slipLegs.map((leg) => ({
        e: leg.event,
        m: leg.market,
        p: leg.pick,
        o: leg.odds,
        f: leg.fairOdds,
        v: Math.round(leg.evPercent * 10) / 10,
        k: leg.bookie,
        t: leg.sport,
        d: leg.eventDate || undefined,
      })),
    };
    const created = await generateParlayShareUrl(encodeParlayForShare(payload), false);
    if (!created) {
      setError("Couldn't create a share link. Try again.");
      return null;
    }
    if (created.limit_reached) {
      setError("Daily share limit reached. Try again tomorrow.");
      return null;
    }
    if (!created.share_id) {
      setError("Share link unavailable.");
      return null;
    }
    const url = `${window.location.origin}${SHARE_PATH}${created.share_id}`;
    shareUrlRef.current = { key, url };
    return url;
  };

  const shareLink = async () => {
    setShareBusy("link");
    setError(null);
    try {
      const url = await ensureShareUrl();
      if (!url) return;
      capture("parlay_slip_shared", { mode: "link", legs: slipLegs.length });
      const ok = await webShareOrCopy({
        url,
        title: `${slipLegs.length}-Leg EV Parlay | BookieBrawler`,
      });
      if (ok) promptBoardToast({ message: "Share link copied." });
    } finally {
      setShareBusy(null);
    }
  };

  const shareToFeed = async () => {
    setShareBusy("feed");
    setError(null);
    try {
      const url = await ensureShareUrl();
      if (!url) return;
      capture("parlay_slip_shared", { mode: "feed", legs: slipLegs.length });
      // Same deep link the feed toast uses — composer pre-fills the URL
      // and attaches the bet-card preview itself.
      parlaySlipOpenSignal.value = false;
      navigate(`/feed?compose=${encodeURIComponent(url)}`);
    } finally {
      setShareBusy(null);
    }
  };

  /* Anchor above the mobile bottom bar (zero-height on desktop). */
  const dockBottom =
    "calc(var(--bb-bottomnav-h, 0px) + env(safe-area-inset-bottom, 0px) + 20px)";

  const pill = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      style={{ position: "fixed", right: 16, bottom: dockBottom, zIndex: 10045 }}
      className="flex items-center gap-2 rounded-full border border-amber-300/50 bg-[#0A1E3B] px-4 py-2.5 shadow-[0_14px_36px_-10px_rgba(2,8,20,0.9)]"
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-300 text-[11px] font-extrabold text-[#0A1E3B]">
        {slipLegs.length}
      </span>
      <span className="max-w-[120px] truncate text-[13px] font-bold uppercase text-white">
        {active.label} slip
      </span>
      <span className="text-[13px] font-bold text-amber-300">{fmtAmerican(math.american)}</span>
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${
          math.evPct > 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"
        }`}
      >
        {math.evPct > 0 ? "+" : ""}
        {math.evPct.toFixed(1)}% EV
      </span>
      {bestUpgradePct > 0.05 && (
        <span
          title={`Better complete price available — switch books in the slip for +${bestUpgradePct.toFixed(1)}% EV`}
          className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-extrabold text-emerald-300"
        >
          ▲ +{bestUpgradePct.toFixed(1)}%
        </span>
      )}
      {books.length > 1 && (
        <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-powder-bloo">
          +{books.length - 1}
        </span>
      )}
    </button>
  );

  const panel = (
    <div
      style={
        isMobile
          ? {
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10045,
              maxHeight: "82vh",
              ...swipe.panelStyle,
            }
          : { position: "fixed", right: 16, bottom: dockBottom, zIndex: 10045, width: 372, maxHeight: "min(76vh, 640px)" }
      }
      className={`flex flex-col border border-amber-300/30 bg-[#010f24] shadow-[0_0_40px_rgba(0,0,0,0.6)] ${
        isMobile ? "rounded-t-2xl" : "rounded-xl"
      }`}
    >
      {isMobile && (
        <div
          className="flex justify-center pt-2"
          aria-hidden="true"
          {...swipe.dragHandleProps}
        >
          <div className="h-1 w-9 rounded-full bg-white/20" />
        </div>
      )}

      <div
        className="flex items-center justify-between px-4 pb-2 pt-3"
        {...(isMobile ? swipe.dragHandleProps : {})}
      >
        <div>
          <p className="font-display text-xs font-bold uppercase tracking-wide text-amber-300/80">
            EV parlay slip
          </p>
          <p className="flex items-center gap-1.5 text-[11px] text-powder-bloo/60">
            {slipLegs.length} {slipLegs.length === 1 ? "leg" : "legs"} at
            <BookLogo
              bookKey={active.label}
              title={active.label}
              chipClassName="flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded bg-white/10 text-[7px] font-bold text-white"
              imgClassName="h-full w-full object-contain"
            />
            <span className="uppercase">{active.label}</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => clearBookSlip(active.label)}
            className="rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-powder-bloo/60 hover:text-powder-bloo"
          >
            Clear
          </button>
          <button
            type="button"
            title="Minimize slip"
            onClick={() => setOpen(false)}
            className="rounded p-1.5 text-powder-bloo/70 hover:bg-white/5 hover:text-white"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6.5 8 10.5 12 6.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {books.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto px-4 pb-2">
          {books.map((book) => (
            <button
              key={book.key}
              type="button"
              onClick={() => {
                activeSlipBookSignal.value = book.key;
              }}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                book.key === active.key
                  ? "border-amber-300/70 bg-amber-300/15 text-amber-300"
                  : "border-white/15 text-powder-bloo/70 hover:bg-white/5"
              }`}
            >
              {book.label}
              <span className="rounded-full bg-white/10 px-1.5 text-[9px]">{book.legs.length}</span>
            </button>
          ))}
        </div>
      )}

      <div
        className="min-h-0 flex-1 overflow-y-auto border-t border-white/5 px-4 py-2"
        ref={isMobile ? swipe.scrollRef : undefined}
        {...(isMobile ? swipe.scrollBodyProps : {})}
      >
        {slipLegs.map((leg) => (
          <div
            key={slipLegKey(leg)}
            className="flex items-start justify-between gap-3 border-b border-white/5 py-2.5 last:border-b-0"
          >
            <div className="min-w-0">
              <p className="truncate text-[13px] font-bold text-white">{leg.pick}</p>
              <p className="truncate text-[11px] text-powder-bloo/60">
                {leg.event} · {leg.market}
              </p>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-powder-bloo/50">
                {leg.bookie}
                <span className="ml-1.5 text-emerald-300/90">
                  {leg.evEstimated ? "~" : "+"}
                  {leg.evPercent.toFixed(1)}% EV
                </span>
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-[13px] font-bold text-amber-300">{fmtAmerican(leg.odds)}</span>
              <button
                type="button"
                title="Remove leg"
                onClick={() => removeSlipLeg(slipLegKey(leg))}
                className="rounded p-1 text-powder-bloo/50 hover:bg-white/5 hover:text-red-300"
              >
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10 px-4 py-3">
        {slipLegs.length < 2 && (
          <p className="mb-2 text-[11px] text-powder-bloo/60">
            Add at least one more leg to build a parlay.
          </p>
        )}
        {math.hasSameEventLegs && (
          <p className="mb-2 text-[11px] text-amber-300/80">
            Two legs share an event — books price same-game parlays with correlation, so the real payout may differ.
          </p>
        )}
        {slipLegs.some((l) => l.evEstimated) && (
          <p className="mb-2 text-[11px] text-powder-bloo/50">
            ~ Sharp legs assume the Sharp track's average realized return; their fair odds are estimates.
          </p>
        )}

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-white/5 px-2 py-1.5">
            <p className="text-[9px] font-bold uppercase tracking-wide text-powder-bloo/50">Odds</p>
            <p className="text-sm font-extrabold text-white">{fmtAmerican(math.american)}</p>
          </div>
          <div className="rounded-lg bg-white/5 px-2 py-1.5">
            <p className="text-[9px] font-bold uppercase tracking-wide text-powder-bloo/50">Hit chance</p>
            <p className="text-sm font-extrabold text-white">{(math.trueProb * 100).toFixed(1)}%</p>
          </div>
          <div className="rounded-lg bg-white/5 px-2 py-1.5">
            <p className="text-[9px] font-bold uppercase tracking-wide text-powder-bloo/50">Parlay EV</p>
            <p className={`text-sm font-extrabold ${math.evPct > 0 ? "text-emerald-300" : "text-red-300"}`}>
              {math.evPct > 0 ? "+" : ""}
              {math.evPct.toFixed(1)}%
            </p>
          </div>
        </div>

        {requotes.length > 0 && (
          <div className="mt-2">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[9px] font-bold uppercase tracking-wide text-powder-bloo/50">
                This parlay at other books
              </p>
              {/* Only complete quotes make the list — a book missing any
                  leg never shows, so these numbers are apples-to-apples. */}
              <p className="text-[9px] text-powder-bloo/40">
                all {slipLegs.length} legs priced
              </p>
            </div>
            <div className="mt-1 flex flex-col gap-1">
              {requotes.map((rq) => {
                const betterBy = rq.evPct - math.evPct;
                const better = betterBy > 0.05;
                return (
                  <button
                    key={rq.key}
                    type="button"
                    title={`Switch this slip to ${rq.label}`}
                    onClick={() => switchBook(rq)}
                    className={`flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-left ${
                      better
                        ? "border-emerald-400/40 bg-emerald-500/[0.07] hover:bg-emerald-500/[0.14]"
                        : "border-white/10 hover:bg-white/5"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <BookLogo
                        bookKey={rq.label}
                        title={rq.label}
                        chipClassName="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded bg-white/10 text-[8px] font-bold text-white"
                        imgClassName="h-full w-full object-contain"
                      />
                      <span className="truncate text-[11px] font-bold uppercase tracking-wide text-powder-bloo">
                        {rq.label}
                      </span>
                      {better && (
                        <span className="shrink-0 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-extrabold text-emerald-300">
                          ▲ +{betterBy.toFixed(1)}%
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-[12px] font-bold text-amber-300">{fmtAmerican(rq.american)}</span>
                      <span
                        className={`text-[11px] font-extrabold ${
                          rq.evPct > 0 ? "text-emerald-300" : "text-red-300"
                        }`}
                      >
                        {rq.evPct > 0 ? "+" : ""}
                        {rq.evPct.toFixed(1)}%
                      </span>
                      <svg viewBox="0 0 16 16" className="h-3 w-3 text-powder-bloo/50" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                );
              })}
            </div>
            {requotes.every((rq) => rq.evPct - math.evPct <= 0.05) && (
              <p className="mt-1 flex items-center gap-1 text-[10px] font-bold text-emerald-300/80">
                <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M3 8.5 6.5 12 13 4.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Best complete price — {active.label}
              </p>
            )}
          </div>
        )}

        <div className="mt-3 flex items-end gap-3">
          <label className="block w-28 shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-wide text-powder-bloo/60">Stake $</span>
            <input
              type="number"
              min={1}
              step="1"
              inputMode="decimal"
              value={stake}
              onChange={(e) => {
                setStakeTouched(true);
                setStake(e.target.value);
              }}
              className="mt-1 w-full rounded border border-[#94c2dc]/30 bg-[#010816] px-2 py-1.5 text-sm text-white"
            />
          </label>
          <div className="min-w-0 flex-1 pb-0.5">
            <p className="text-[11px] text-powder-bloo/60">
              To win <b className="text-white">{stakeValid ? fmtUsd(toWin) : "—"}</b>
            </p>
            <p className="truncate text-[10px] text-powder-bloo/45">
              {math.unit
                ? `Rec ${fmtUsd(math.recommendedStake)} · 1% unit of ${fmtUsd(math.bankroll)}, no market edge`
                : `Rec ${fmtUsd(Math.max(math.recommendedStake, 0))} · ${math.kellyMult}× Kelly of ${fmtUsd(math.bankroll)}`}
            </p>
          </div>
        </div>

        {error != null && <p className="mt-2 text-xs font-bold text-red-300/90">{error}</p>}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={shareBusy != null || slipLegs.length < 2}
            onClick={() => void shareLink()}
            className="rounded-lg border border-[#94c2dc]/30 py-2 text-xs font-bold uppercase tracking-wide text-powder-bloo hover:bg-white/5 disabled:opacity-40"
          >
            {shareBusy === "link" ? "Linking…" : "Copy link"}
          </button>
          <button
            type="button"
            disabled={shareBusy != null || slipLegs.length < 2}
            onClick={() => void shareToFeed()}
            className="rounded-lg border border-[#94c2dc]/30 py-2 text-xs font-bold uppercase tracking-wide text-powder-bloo hover:bg-white/5 disabled:opacity-40"
          >
            {shareBusy === "feed" ? "Opening…" : "Post to feed"}
          </button>
        </div>

        <button
          type="button"
          disabled={saving || slipLegs.length < 2 || !stakeValid}
          onClick={() => void trackParlay()}
          className="mt-2 w-full rounded-lg border border-[#00ffff]/50 bg-[#00ffff]/15 py-2 text-xs font-bold uppercase tracking-wide text-[#00ffff] hover:bg-[#00ffff]/25 disabled:opacity-40"
          style={isMobile ? { marginBottom: "env(safe-area-inset-bottom, 0px)" } : undefined}
        >
          {saving ? "Tracking…" : "Track parlay in My Bets"}
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      {open && isMobile && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 10044, background: "rgba(0,0,0,0.6)" }}
          onClick={() => setOpen(false)}
        />
      )}
      {open ? panel : pill}
    </>,
    document.body
  );
};

export default ParlaySlipDock;
